import React from "react";

import { Divider } from "~/components/divider";
import { Form } from "~/components/form";
import { FormField } from "~/components/form/field";
import { FormLabel } from "~/components/form/label";
import { FormFieldReset } from "~/components/form/reset";
import { Input } from "~/components/input";
import { RadioButtonGroup } from "~/components/radio-button-group";
import { RadioButtonGroupItem } from "~/components/radio-button-group/item";
import { useScoped } from "~/tools/impulse";
import { onIdle } from "~/tools/on-idle";
import type { Record } from "~/tools/record";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import type { DashboardVariableInitConfig, DashboardVariableKind } from "../../common/dashboard-spec";
import { Widget, WidgetBody, WidgetHeader } from "../_widget";

import { FormContextProvider, type FormContextValue } from "./_form-context";
import { FORM_ID } from "./_form-id";
import { type FormState, Processor, VARIABLE_TYPE_OPTIONS } from "./_form-state";
import { ListVariableForm } from "./_list-variable";
import { Preview } from "./_preview";
import { TextVariableForm } from "./_text-variable";

function useFocusInput() {
	const inputRef = React.useRef<HTMLInputElement>(null);

	React.useEffect(() => {
		// the drawer does not allow to autoFocus the input
		// neither .focus() during synchronous effect
		return onIdle(() => {
			inputRef.current?.focus();
		});
	}, []);

	return inputRef;
}

const TYPE_TO_LABEL = {
	TextVariable: "Text variable",
	ListVariable: "List variable",
} satisfies Record<DashboardVariableKind>;

export interface DrawerContentProps {
	isEditing: boolean;
	timeRange: TimeRange;
	state: FormState;
	onSave: (output: DashboardVariableInitConfig) => void;
}

export const DrawerContent: React.FC<DrawerContentProps> = ({ isEditing = false, timeRange, state, onSave }) => {
	const nameInputRef = useFocusInput();
	const activeType = useScoped((scope) => state.fields.type.getActiveBranch(scope), [state]);
	const context = React.useMemo<FormContextValue>(() => ({ timeRange }), [timeRange]);

	return (
		<FormContextProvider value={context}>
			<div className="grid h-full grid-cols-2 gap-3">
				<Form
					className="space-y-4 overflow-y-auto pr-3"
					id={FORM_ID}
					onSubmit={(output) => {
						onSave(Processor.toSpec(output));
					}}
					state={state}
				>
					<FormField state={state.fields.name}>
						<FormLabel asChild>
							<div>
								<FormLabel>Name *</FormLabel>
								<FormFieldReset />
							</div>
						</FormLabel>

						{(scope, field) => (
							<Input
								name="dashboard-variable-name"
								onChange={(event) => field.setInput(event.target.value)}
								ref={nameInputRef}
								value={field.getInput(scope)}
							/>
						)}
					</FormField>

					<FormField state={state.fields.label}>
						<FormLabel asChild>
							<div>
								<FormLabel>Display Label</FormLabel>
								<FormFieldReset />
							</div>
						</FormLabel>

						{(scope, field) => (
							<Input
								name="dashboard-variable-label"
								onChange={(event) => field.setInput(event.target.value)}
								value={field.getInput(scope)}
							/>
						)}
					</FormField>

					<FormField state={state.fields.description}>
						<FormLabel asChild>
							<div>
								<FormLabel>Description</FormLabel>
								<FormFieldReset />
							</div>
						</FormLabel>

						{(scope, field) => (
							<Input
								name="dashboard-variable-description"
								onChange={(event) => field.setInput(event.target.value)}
								value={field.getInput(scope)}
							/>
						)}
					</FormField>

					<FormField state={state.fields.type.active}>
						<FormLabel asChild>
							<div>
								<FormLabel>Type</FormLabel>
								{isEditing && <FormFieldReset />}
							</div>
						</FormLabel>

						{(scope, field) => (
							<RadioButtonGroup onValueChange={(value) => field.setInput(value)} value={field.getInput(scope)}>
								{VARIABLE_TYPE_OPTIONS.map((option) => (
									<RadioButtonGroupItem key={option} name="dashboard-variable-type" value={option}>
										{TYPE_TO_LABEL[option]}
									</RadioButtonGroupItem>
								))}
							</RadioButtonGroup>
						)}
					</FormField>

					<Divider className="uppercase tracking-widest">
						{activeType?.kind === "TextVariable" && "Text options"}
						{activeType?.kind === "ListVariable" && "List options"}
					</Divider>

					{activeType?.kind === "TextVariable" && <TextVariableForm state={activeType.value} />}

					{activeType?.kind === "ListVariable" && (
						<ListVariableForm isEditing={isEditing} name={state.fields.name} state={activeType.value} />
					)}
				</Form>

				<Widget className="min-h-0">
					<WidgetHeader title="Preview" />

					<WidgetBody card className="overflow-y-auto">
						<Preview name={state.fields.name} state={state} />
					</WidgetBody>
				</Widget>
			</div>
		</FormContextProvider>
	);
};
